import { type GridRowId } from '@mui/x-data-grid-pro';
import { type DateRange } from '@mui/x-date-pickers-pro';
import * as moment from 'moment';
import { type Moment } from 'moment';

import type APIAddress from '~/types/APIAddress';
import type APIMessage from '~/types/APIMessage';
import type BusinessRule from '~/types/BusinessRule';
import type Order from '~/types/Order';
import type Rate from '~/types/Rate';
import type Shipment from '~/types/Shipment';
import type Transaction from '~/types/Transaction';

import {
  type BaseSearchCallbacks,
  type BaseSearchParameters,
} from '~/blackbeard/components/Search/types';
import { type currencies } from '~/constants/currency';
import { weightUnitMap } from '~/constants/weight';
import { type SortFieldType } from '~/modules/filters/types/filters.type';
import { type OrderShipmentV2, type OrderV2 } from '~/modules/orders/types/ordersV2.type';
import { type ShipmentsTableRow } from '~/pages/Shipments/ShipmentsPage/ShipmentsTable/types';
import { isFulfilledOutsideShippo, type OrderTag, type OrdersRenderData } from '~/types/Order';
import { type BatchEntryExtended } from '~/types/OrderBatchEntry';
import { type BaseRate } from '~/types/Rate';
import { StoreName, getStorePlatformFilterValue, type filterStoreNameValues } from '~/types/Store';
import { filterRulesToShow } from '~/utils/businessRules';

import { searchParameterToSearchQuery, searchQueryToSearchParameter } from './utils';

export type RowType = 'partial-child' | 'partial-parent' | 'single';

export type StorePlatformFilterName = 'all' | StoreName;

const DEFAULT_PER_PAGE_LIMIT = 25;
export type PageLimitOptions = 25 | 50 | 100;
export const pageLimitOptions: PageLimitOptions[] = [25, 50, 100];

export const weightUnitFilterTypeList = ['g', 'kg', 'oz', 'lb', ''] as const;
export type WeightUnitFilterType = (typeof weightUnitFilterTypeList)[number];
export type IsInternationalFilterType = '' | 'false' | 'true';
export type DestinationFilterType = 'domestic' | 'international';
export type OrderByFilterType = 'asc' | 'desc';
export type SortByFilterType = 'created_date' | 'relevance';
export type OrderStatusFilterType =
  | 'awaitpay'
  | 'cancelled'
  | 'paid'
  | 'partially_fulfilled'
  | 'refunded'
  | 'shipped'
  | 'unknown';

export type SenderAddressFilterEntry = {
  city: string;
  name: string;
  objectId?: string;
  street: string;
};

export const senderAddressFilterEntriesEqual = (
  left: SenderAddressFilterEntry,
  right: SenderAddressFilterEntry,
): boolean => left.name === right.name && left.city === right.city && left.street === right.street;

export const senderFilterEntryToChipId = (entry: SenderAddressFilterEntry): string =>
  `sender-${senderFilterEntryToKey(entry)}`;

export const senderFilterEntryToKey = (entry: SenderAddressFilterEntry): string => {
  if (entry.objectId) return `id:${encodeURIComponent(entry.objectId)}`;

  return [
    'fields',
    encodeURIComponent(entry.name),
    encodeURIComponent(entry.city),
    encodeURIComponent(entry.street),
  ].join(':');
};

export const chipIdToSenderFilterEntryKey = (chipId: string): null | string => {
  if (!chipId.startsWith('sender-')) return null;

  const encodedKey = chipId.slice('sender-'.length);
  return encodedKey ? encodedKey : null;
};

export const formatSenderFilterChipLabel = (entry: SenderAddressFilterEntry): string => {
  const name = entry.name.trim();
  if (name) return name;
  const street = entry.street.trim();
  if (street) return street;
  return entry.city.trim();
};

const isSenderAddressFilterEntry = (value: unknown): value is SenderAddressFilterEntry => {
  if (value === null || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.name === 'string' && typeof row.city === 'string' && typeof row.street === 'string'
  );
};

/**
 * Reads `address_from[0]`, `address_from[1]`, … where each value is JSON
 * `{ "name", "city", "street" }` (single query param family for the API).
 */
const senderAddressFilterQueryToState = (
  searchQuery: Record<string, unknown>,
): SenderAddressFilterEntry[] => {
  const byIndex = new Map<number, SenderAddressFilterEntry>();

  for (const [key, rawValue] of Object.entries(searchQuery)) {
    if (rawValue === undefined || rawValue === '') continue;

    const match = /^address_from\[(\d+)]$/.exec(key);
    if (!match) continue;

    const index = Number.parseInt(match[1], 10);
    const asString = String(rawValue);

    let parsed: unknown;
    try {
      parsed = JSON.parse(asString) as unknown;
    } catch {
      continue;
    }

    if (!isSenderAddressFilterEntry(parsed)) continue;

    byIndex.set(index, {
      city: parsed.city,
      name: parsed.name,
      street: parsed.street,
    });
  }

  return [...byIndex.entries()]
    .sort(([leftIndex], [rightIndex]) => leftIndex - rightIndex)
    .map(([, row]) => row)
    .filter((row) => row.name || row.city || row.street);
};

const senderAddressFilterStateToQueryParams = (
  entries: SenderAddressFilterEntry[],
): Record<string, string> => {
  const out: Record<string, string> = {};
  entries.forEach((entry, index) => {
    out[`address_from[${index}]`] = JSON.stringify({
      city: entry.city,
      name: entry.name,
      street: entry.street,
    });
  });
  return out;
};

export type OrdersSearchParameters = {
  createdByFilter: 'all' | string;
  currentPageNumber: number;
  currentPerPage: PageLimitOptions;
  dateFilter: DateRange<Moment>;
  hidden: boolean;
  isInternational: IsInternationalFilterType;
  itemFilter: string[];
  orderStatus: 'all' | 'cancelled' | 'fulfilled' | 'hidden' | 'pending';
  orderTagsFilter: { tags: string[]; untagged: boolean };
  quantityFilter: { maxQuantity: string; minQuantity: string };
  rowCount: number;
  saved: string;
  senderAddressFilter: SenderAddressFilterEntry[];
  shippingMethodsFilter: string[];
  sortField: SortFieldType;
  storeFilter: string[];
  storePlatformFilter: StorePlatformFilterName;
  weightFilter: { maxWeight: string; minWeight: string; weightUnit: WeightUnitFilterType };
} & BaseSearchParameters;

export type OrderSearchParameterKeys = keyof OrdersSearchParameters;

export type OrdersSearchCallbacks = {
  [k in keyof OrdersSearchParameters]: (value: OrdersSearchParameters[k]) => void;
} & BaseSearchCallbacks;

export interface OrdersSearchForm {
  searchCallbacks: OrdersSearchCallbacks;
  searchDirty: boolean;
  searchQueryParam: object | undefined;
  searchState: OrdersSearchParameters;
}

// feron needs to support query params of both forms:
// 1. shipping_method[]
// 2. shipping_method[0], shipping_method[1]
type AddressFromKey = `address_from[${number}]`;
type ShippingMethodKey = `shipping_method[${number}]`;

// TypeScript won't allow template literals in typed objects directly, so need to compose a Record object to support them
type AddressFromRecord = Record<AddressFromKey, string>;
type ShippingMethodRecord = Record<ShippingMethodKey, string>;

export type OrdersSearchQueryParameters = {
  end_date?: string;
  hidden?: string;
  is_international?: IsInternationalFilterType;
  'item[]'?: string;
  order_by?: string;
  order_status?: 'all' | 'cancelled' | 'fulfilled' | 'hidden' | 'pending';
  page?: string;
  q?: string;
  results?: string;
  'shipping_method[]'?: string;
  shop_app?: string;
  sort_by?: string;
  'source[]'?: string;
  start_date?: string;
  total_item_quantity_max?: string;
  total_item_quantity_min?: string;
  weight_gt?: string;
  weight_lt?: string;
  weight_unit?: string;
} & AddressFromRecord &
  ShippingMethodRecord;

export const defaultOrdersSearchParams: OrdersSearchParameters = {
  createdByFilter: 'all',
  currentPageNumber: 1,
  currentPerPage: DEFAULT_PER_PAGE_LIMIT,
  dateFilter: [null, null],
  hidden: false,
  isInternational: '',
  itemFilter: [],
  orderStatus: 'all',
  orderTagsFilter: { tags: [], untagged: false },
  quantityFilter: { maxQuantity: '', minQuantity: '' },
  rowCount: 0,
  saved: '',
  searchBarQuery: '',
  searchError: false,
  senderAddressFilter: [],
  shippingMethodsFilter: [],
  sortField: '-created_at',
  storeFilter: [],
  storePlatformFilter: 'all',
  weightFilter: { maxWeight: '', minWeight: '', weightUnit: '' },
};

type StoreSearchQueryToSearchState = {
  [k in keyof typeof filterStoreNameValues]: StorePlatformFilterName;
};

const searchQueryToSearchStateStoreValues = Object.keys(StoreName).reduce<
  Partial<StoreSearchQueryToSearchState>
>(
  (accum, key) => {
    const value = StoreName[key as keyof typeof StoreName];
    accum[getStorePlatformFilterValue(value)] = value;
    return accum;
  },
  // This looks like a bug. `All` is not a valid value but `all` is.
  // I am asserting the type to avoid changing code but this should be checked.
  { undefined: 'All' as StoreSearchQueryToSearchState[keyof StoreSearchQueryToSearchState] },
);

export const shippingMethodsQueryToState = (searchQuery: OrdersSearchQueryParameters) =>
  Object.entries(searchQuery)
    .filter(([key]) => key.includes('shipping_method'))
    .reduce<string[]>((accum, [, value]) => {
      if (value) accum.push(value);

      return accum;
    }, []);

export const shippingMethodsStateToQuery = (
  shippingMethodsFilter: string[],
): ShippingMethodRecord =>
  shippingMethodsFilter.reduce<Record<string, string>>((accum, item, index) => {
    accum[`shipping_method[${index}]`] = item;
    return accum;
  }, {});

export const ordersSearchQueryToSearchState = (
  searchQuery: OrdersSearchQueryParameters,
): OrdersSearchParameters => {
  const searchParameters: Partial<OrdersSearchParameters> = {};
  if (searchQuery['start_date'] || searchQuery['end_date']) {
    const startDate = searchQuery['start_date'] ? moment(searchQuery['start_date']) : null;
    const endDate = searchQuery['end_date'] ? moment(searchQuery['end_date']) : null;
    searchParameters['dateFilter'] = [startDate, endDate];
  }

  if (searchQuery['weight_gt'] || searchQuery['weight_lt'] || searchQuery['weight_unit']) {
    const minWeight = searchQuery['weight_gt'] ? searchQuery['weight_gt'] : '';
    const maxWeight = searchQuery['weight_lt'] ? searchQuery['weight_lt'] : '';
    const weightUnit = searchQuery['weight_unit'] ? searchQuery['weight_unit'] : 'lb';
    const fallbackWeightUnit = weightUnitMap[weightUnit] || 'lb';
    searchParameters['weightFilter'] = { maxWeight, minWeight, weightUnit: fallbackWeightUnit };
  }

  if (searchQuery['total_item_quantity_min'] || searchQuery['total_item_quantity_max']) {
    const minQuantity = searchQuery['total_item_quantity_min'] || '';
    const maxQuantity = searchQuery['total_item_quantity_max'] || '';
    searchParameters['quantityFilter'] = { maxQuantity, minQuantity };
  }

  if (searchQuery['order_status']) searchParameters['orderStatus'] = searchQuery['order_status'];

  if (searchQuery['sort_by'] === 'created_at') {
    const sortField = searchQuery['order_by'] === 'desc' ? '-created_at' : 'created_at';
    searchParameters['sortField'] = sortField;
  }

  if (searchQuery['sort_by'] === '_score') searchParameters['sortField'] = 'most_relevant';

  if (searchQuery['is_international'])
    searchParameters['isInternational'] = searchQuery['is_international'];

  searchParameters['storeFilter'] = searchQueryToSearchParameter(searchQuery, 'source');

  if (searchQuery['shop_app']) {
    const shopAppValue = searchQuery['shop_app'];
    searchParameters['storePlatformFilter'] = searchQueryToSearchStateStoreValues[shopAppValue];
  }

  if (searchQuery['page'])
    searchParameters['currentPageNumber'] = parseInt(searchQuery['page'], 10);

  if (searchQuery['results'])
    searchParameters['currentPerPage'] = parseInt(searchQuery['results'], 10) as PageLimitOptions;

  if (searchQuery['hidden']) searchParameters['hidden'] = searchQuery['hidden'] === 'true';

  if (searchQuery['saved']) searchParameters['saved'] = searchQuery['saved'];

  searchParameters['itemFilter'] = searchQueryToSearchParameter(searchQuery, 'item');
  searchParameters['senderAddressFilter'] = senderAddressFilterQueryToState(searchQuery);
  searchParameters['shippingMethodsFilter'] = shippingMethodsQueryToState(searchQuery);

  searchParameters['orderTagsFilter'] = {
    tags: searchQueryToSearchParameter(searchQuery, 'tag'),
    untagged: searchQuery['untagged'] === 'true',
  };

  searchParameters['searchBarQuery'] = searchQuery['q'] || '';
  return {
    ...defaultOrdersSearchParams,
    ...searchParameters,
  };
};

function filterKeyIsKeyofSearchState(_key: string): asserts _key is keyof OrdersSearchParameters {
  return;
}

export const ordersSearchStateToSearchQuery = (
  searchState: OrdersSearchParameters,
): OrdersSearchQueryParameters => {
  const queryParams: OrdersSearchQueryParameters = {};
  const sortParameters = (sortField: SortFieldType) => {
    switch (sortField) {
      case '-created_at': {
        queryParams['order_by'] = 'desc';
        queryParams['sort_by'] = 'created_at';
        break;
      }
      case 'created_at': {
        queryParams['order_by'] = 'asc';
        queryParams['sort_by'] = 'created_at';
        break;
      }
      case 'most_relevant':
      default: {
        queryParams['order_by'] = 'desc';
        queryParams['sort_by'] = '_score';
        break;
      }
    }
  };

  for (const filterKey in searchState) {
    filterKeyIsKeyofSearchState(filterKey);
    if (
      JSON.stringify(searchState[filterKey]) !==
      JSON.stringify(defaultOrdersSearchParams[filterKey])
    ) {
      switch (filterKey) {
        case 'sortField': {
          sortParameters(searchState.sortField);
          break;
        }
        case 'shippingMethodsFilter': {
          const shippingMethodParams = shippingMethodsStateToQuery(
            searchState.shippingMethodsFilter,
          );
          Object.entries(shippingMethodParams).forEach(([key, value]) => {
            // This looks like a bug of some sort but I'm not sure what it's doing.
            // The compiler says this isn't possible. I tried to assert the key's type as `key as keyof OrdersSearchQueryParameters` and it's becoming `never`.
            // I'm setting `queryParams` as any until someone can investigate.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (queryParams as any)[key] = value;
          });
          break;
        }
        case 'orderStatus': {
          queryParams.order_status = searchState[filterKey];
          break;
        }
        case 'isInternational': {
          queryParams['is_international'] = searchState[filterKey];
          break;
        }
        case 'dateFilter': {
          const [startDate, endDate] = searchState[filterKey];
          if (moment.isMoment(startDate)) queryParams['start_date'] = startDate.format();

          if (moment.isMoment(endDate)) queryParams['end_date'] = endDate.format();

          break;
        }
        case 'storeFilter': {
          const filterValue = searchState[filterKey];
          searchParameterToSearchQuery(filterValue, queryParams, 'source');
          break;
        }
        case 'storePlatformFilter': {
          const filterValue = searchState[filterKey];
          if (filterValue !== 'all')
            queryParams['shop_app'] = getStorePlatformFilterValue(filterValue);

          break;
        }
        case 'weightFilter': {
          const { maxWeight, minWeight, weightUnit } = searchState[filterKey];
          queryParams['weight_gt'] = minWeight;
          queryParams['weight_lt'] = maxWeight;
          queryParams['weight_unit'] = weightUnit;
          break;
        }
        case 'quantityFilter': {
          const { maxQuantity, minQuantity } = searchState[filterKey];
          if (minQuantity) queryParams['total_item_quantity_min'] = minQuantity;
          if (maxQuantity) queryParams['total_item_quantity_max'] = maxQuantity;
          break;
        }
        case 'searchBarQuery': {
          const filterValue = searchState[filterKey];
          queryParams['q'] = filterValue;
          break;
        }
        case 'currentPerPage': {
          const filterValue = searchState[filterKey];
          queryParams['results'] = String(filterValue);
          break;
        }
        case 'currentPageNumber': {
          const filterValue = searchState[filterKey];
          queryParams['page'] = parseInt(String(filterValue), 10).toString();
          break;
        }
        case 'hidden': {
          const filterValue = searchState[filterKey];
          queryParams['hidden'] = String(filterValue);
          break;
        }
        case 'itemFilter': {
          const filterValue = searchState[filterKey];
          searchParameterToSearchQuery(filterValue, queryParams, 'item');
          break;
        }
        case 'senderAddressFilter': {
          const senderParams = senderAddressFilterStateToQueryParams(
            searchState.senderAddressFilter,
          );
          Object.entries(senderParams).forEach(([key, value]) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (queryParams as any)[key] = value;
          });
          break;
        }
        case 'saved': {
          const filterValue = searchState[filterKey];
          queryParams['saved'] = String(filterValue);
          break;
        }
        case 'orderTagsFilter': {
          const filterValue = searchState[filterKey];
          searchParameterToSearchQuery(filterValue.tags, queryParams, 'tag');
          queryParams['untagged'] = String(filterValue.untagged);
          break;
        }
        default: {
          break;
        }
      }
    }
  }

  const selectedKeys = Object.keys(queryParams);
  if (selectedKeys.length === 0 || (selectedKeys.length === 1 && !!queryParams.page))
    return queryParams;

  sortParameters(searchState.sortField);

  return queryParams;
};

export type OrderTableRow =
  | ({ id: GridRowId; rowIndex: number } & Partial<PopulatedOrderTableRow>)
  | PopulatedOrderTableRow;

export const isRowPopulated = (
  row?: OrderTableRow | ShipmentsTableRow | null,
): row is PopulatedOrderTableRow => !!row?.order;

export type PopulatedV2OrderTableRow = {
  id: string;
  orderV2: NonNullable<PopulatedOrderTableRow['orderV2']>;
  rowIndex: number;
};

export const isRowV2Populated = (row?: OrderTableRow | null): row is PopulatedV2OrderTableRow =>
  !!row?.orderV2;

type ExpandedV2Entity<T> = {
  data: T;
  isLoading: boolean;
};

export interface PopulatedOrderTableRow {
  action: {
    batchEntry?: BatchEntryExtended;
    order: Order;
    rate?: { amount: number; currency: keyof typeof currencies; provider: string };
    transactionErrorMessages?: APIMessage[];
    transactionId?: string;
  };
  child: boolean;
  customer: {
    batchEntry?: BatchEntryExtended;
    kind: RowType;
    order: Order;
  };
  // Used for sorting
  date: number;
  id: string;
  items: {
    batchEntry?: BatchEntryExtended;
    fulfilledExternally: boolean;
    kind: RowType;
    order: Order;
  };
  menu: {
    commercialInvoiceUrl: Maybe<string>;
    order: Order;
  };
  order: {
    kind: RowType;
    order: Order;
    // Used for identifying split orders
    partialFulfillmentRank: number;
    rules?: BusinessRule[];
  };
  orderData: OrdersRenderData;
  orderV2?: {
    expanded: {
      selectedRate: ExpandedV2Entity<Rate | undefined>;
      shipment: ExpandedV2Entity<Shipment | undefined>;
      tags: ExpandedV2Entity<OrderTag[]>;
    };
    kind: RowType;
    order: OrderV2;
    orderShipment: OrderShipmentV2;
    priority: number;
  };
  packageInformation: {
    batchEntry?: BatchEntryExtended;
    fulfilledExternally: boolean;
    kind: RowType;
    order: Order;
  };
  priority: number; // used for backend sort ranking
  rates: {
    batchEntry?: BatchEntryExtended;
    kind: RowType;
    loading: boolean;
    order: Order;
    selectedRate?: Rate;
    shipment?: Shipment;
    trackingRate?: BaseRate;
    transaction?: Transaction;
    validUserAddress?: APIAddress;
  };
  rowIndex: number;
  tags: {
    isLoading: boolean;
    list: OrderTag[];
  };
}

// This type is pointless...
// ...but it might help someone understand the relationship between `orderTableRowKey` and `dissectOrderTableRowKey` as this file grows
type OrderTableRowKey = string;

// We need a unique ID for each row. An order ID might appear more than once for split orders.
// Exported so non-grid callers (e.g. sagas reconstructing the row model) build the exact same key.
export const buildOrderTableRowKey = (orderObjectId: string, rank?: number): OrderTableRowKey =>
  rank ? `${orderObjectId}-${rank}` : `${orderObjectId}`;

const orderTableRowKey = (orderData: OrdersRenderData, rank?: number): OrderTableRowKey =>
  buildOrderTableRowKey(orderData.order.objectId, rank);

export const dissectOrderTableRowKey = (key: OrderTableRowKey | number) => {
  const [orderObjectId] = `${key}`.split('-');
  return {
    orderObjectId,
  } as const;
};

export const orderTableRow = (
  orderData: OrdersRenderData,
  isLoadingOrderTags: boolean,
  getRateForRow: (shipmentObjectId?: string) => Rate | undefined,
  getBatchEntryIsLoading: (batchEntry?: BatchEntryExtended) => boolean,
  getShipmentByObjectId: (shipmentObjectId?: string) => Shipment | undefined,
  rowIndex: number,
): OrderTableRow => {
  const rateForRow = getRateForRow(orderData.batchEntry?.shipmentObjectId || undefined);
  const transactionId = orderData.batchEntry?.transactionObjectId;
  // TODO: This complexity hurts! Can we find a way to speed this up so we're not doing loops in loops?
  const transaction = orderData.batchEntry
    ? orderData.order.transactions.find((t) => t.objectId === transactionId)
    : orderData.order.transactions[orderData.order.transactions.length - 1];
  const trackingRate = transaction
    ? orderData.order.rates.find((r) => r.objectId === transaction.rate)
    : undefined;

  const fulfilledExternally = isFulfilledOutsideShippo(orderData.order);
  const tags = orderData.order.tags || [];

  // TODO: The ID might cause problems if an order's batch entry ID changes. We'll need to explicitly remove the old row before adding the new one.
  // Alternatively, we could create some kind of composite key that does not take the batch entry into account.
  return {
    action: {
      batchEntry: orderData.batchEntry || undefined,
      order: orderData.order,
      rate: rateForRow
        ? {
            amount: Number(rateForRow.amount),
            currency: rateForRow.currency as keyof typeof currencies,
            provider: rateForRow.provider,
          }
        : undefined,
      transactionErrorMessages: transaction?.messages,
      transactionId: transactionId || undefined,
    },
    customer: {
      batchEntry: orderData.batchEntry || undefined,
      kind: orderData.kind,
      order: orderData.order,
    },
    date: orderData.order.placedAt.unix(),
    id: orderTableRowKey(orderData, orderData?.batchEntry?.partialFulfillmentRank),
    items: {
      batchEntry: orderData.batchEntry || undefined,
      fulfilledExternally,
      kind: orderData.kind,
      order: orderData.order,
    },
    menu: {
      commercialInvoiceUrl: transaction?.commercialInvoiceUrl,
      order: orderData.order,
    },
    order: {
      kind: orderData.kind,
      order: orderData.order,
      partialFulfillmentRank: orderData.batchEntry?.partialFulfillmentRank || 0,
      rules: filterRulesToShow(orderData.batchEntry?.rules) || undefined,
    },
    orderData,
    packageInformation: {
      batchEntry: orderData.batchEntry || undefined,
      fulfilledExternally,
      kind: orderData.kind,
      order: orderData.order,
    },
    priority: rowIndex,
    rates: {
      batchEntry: orderData.batchEntry || undefined,
      kind: orderData.kind,
      loading: getBatchEntryIsLoading(orderData.batchEntry || undefined),
      order: orderData.order,
      selectedRate: rateForRow,
      shipment: getShipmentByObjectId(orderData.batchEntry?.shipmentObjectId || undefined),
      trackingRate,
      transaction,
    },
    rowIndex,
    tags: {
      isLoading: isLoadingOrderTags,
      list: tags,
    },
  };
};

export enum BulkDrawerLoadOptions {
  ADDRESS = 'customer',
  DEFAULT = 'default',
  ITEMS = 'items',
  ORDER = 'order',
  PACKAGE = 'packageInformation',
  RATES = 'rates',
}
